import type { GenerationView, KbDocument } from "@cosmosai/api-client";
import { FileTextIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDocuments, useGenerations } from "../../api/knowledge-queries";
import { useFormat } from "../../i18n/format";
import type { KindTabProps } from "../../kinds/tabs";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { DropZone, PickFilesButton } from "../../ui/drop-zone";
import { EmptyState } from "../../ui/empty-state";
import { ErrorState } from "../../ui/error-state";
import { Skeleton } from "../../ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import { DocumentSheet } from "./document-sheet";
import { problemText } from "./problems";
import {
  DOCUMENT_TYPES,
  documentState,
  INGESTION_VARIANT,
  isMoving,
  shownEnvironment,
} from "./status";
import { type UploadRow, useUploads } from "./use-uploads";

/** The knowledge base's documents and their ingestion in the selected environment (5b). */
export function DocumentsTab({ workspace, specRef, environment, environments }: KindTabProps) {
  const { t } = useTranslation();
  const project = specRef.project ?? "";
  const kb = specRef.name;
  const [poll, setPoll] = useState(false);
  const documents = useDocuments(workspace, project, kb, { poll });
  const generations = useGenerations(workspace, project, kb, { poll });
  const moving = isMoving(documents.data, generations.data);
  if (moving !== poll) setPoll(moving);
  const uploads = useUploads(workspace, project, kb);
  const [open, setOpen] = useState<string | null>(null);
  const error = documents.error ?? generations.error;
  const shown = shownEnvironment(environment, environments);
  const pick = (
    <PickFilesButton
      label={t("knowledge.upload.button")}
      onFiles={uploads.start}
      accept={DOCUMENT_TYPES}
      variant="default"
    />
  );
  return (
    <div className="space-y-3" data-testid="kb-documents">
      <DropZone
        label={t("knowledge.upload.drop")}
        onFiles={uploads.start}
        className="flex flex-wrap items-center justify-between gap-3 px-[18px] py-3"
      >
        <p className="text-sm text-ink-2">
          {t("knowledge.upload.hint", { environment: shown ?? "—" })}
        </p>
        {pick}
      </DropZone>
      {uploads.rows.length > 0 && (
        <ul
          aria-live="polite"
          aria-label={t("knowledge.upload.list")}
          className="divide-y divide-line rounded-md border border-line bg-surface"
        >
          {uploads.rows.map((row) => (
            <UploadLine
              key={row.key}
              row={row}
              onCancel={() => uploads.cancel(row.key)}
              onDismiss={() => uploads.dismiss(row.key)}
            />
          ))}
        </ul>
      )}
      {error ? (
        <ErrorState
          error={error}
          action={
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                void documents.refetch();
                void generations.refetch();
              }}
            >
              {t("knowledge.retryLoad")}
            </Button>
          }
        />
      ) : !documents.data || !generations.data ? (
        <Skeleton className="h-40" aria-label={t("knowledge.loading")} />
      ) : documents.data.length === 0 ? (
        <EmptyState
          icon={FileTextIcon}
          title={t("knowledge.empty.title")}
          text={t("knowledge.empty.text")}
          action={pick}
        />
      ) : (
        <DocumentsTable
          documents={documents.data}
          generations={generations.data}
          environment={shown}
          onOpen={setOpen}
        />
      )}
      {open && documents.data && generations.data && (
        <DocumentSheet
          workspace={workspace}
          project={project}
          kb={kb}
          document={documents.data.find((d) => d.id === open)}
          generations={generations.data}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}

function UploadLine({
  row,
  onCancel,
  onDismiss,
}: {
  row: UploadRow;
  onCancel: () => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const percent = Math.round(row.progress * 100);
  return (
    <li className="flex flex-wrap items-center gap-3 px-[18px] py-2.5" data-testid="kb-upload-row">
      <span className="min-w-0 flex-1 truncate font-medium" title={row.name}>
        {row.name}
      </span>
      {row.state === "uploading" || row.state === "waiting" ? (
        <>
          <progress
            value={percent}
            max={100}
            aria-label={t("knowledge.upload.progress", { name: row.name })}
            className="h-2 w-40 accent-primary"
          />
          <Button
            size="sm"
            variant="ghost"
            aria-label={t("knowledge.upload.cancelNamed", { name: row.name })}
            onClick={onCancel}
          >
            {t("knowledge.upload.cancel")}
          </Button>
        </>
      ) : (
        <>
          <span className={row.state === "failed" ? "text-sm text-crit-ink" : "text-sm text-ink-2"}>
            {row.state === "failed"
              ? problemText(t, row.error)
              : t(`knowledge.upload.${row.outcome ?? "queued"}`)}
          </span>
          <Button
            size="sm"
            variant="ghost"
            aria-label={t("knowledge.upload.dismissNamed", { name: row.name })}
            onClick={onDismiss}
          >
            {t("knowledge.upload.dismiss")}
          </Button>
        </>
      )}
    </li>
  );
}

function DocumentsTable({
  documents,
  generations,
  environment,
  onOpen,
}: {
  documents: KbDocument[];
  generations: GenerationView[];
  environment: string | null;
  onOpen: (id: string) => void;
}) {
  const { t } = useTranslation();
  const { bytes, relative, dateTime } = useFormat();
  return (
    <div className="rounded-md border border-line bg-surface">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("knowledge.columns.name")}</TableHead>
            <TableHead>{t("knowledge.columns.type")}</TableHead>
            <TableHead className="text-right">{t("knowledge.columns.size")}</TableHead>
            <TableHead>{t("knowledge.columns.added")}</TableHead>
            <TableHead>
              {t("knowledge.columns.status", { environment: environment ?? "—" })}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {documents.map((document) => (
            <TableRow key={document.id} data-testid={`kb-document-row-${document.id}`}>
              <TableCell>
                <Button
                  variant="link"
                  className="h-auto p-0 font-medium"
                  onClick={() => onOpen(document.id)}
                >
                  {document.name}
                </Button>
              </TableCell>
              <TableCell className="text-ink-2">{document.mediaType ?? "—"}</TableCell>
              <TableCell className="text-right tabular-nums">{bytes(document.sizeBytes)}</TableCell>
              <TableCell>
                <time dateTime={document.createdAt} title={dateTime(document.createdAt)}>
                  {relative(document.createdAt)}
                </time>
              </TableCell>
              <TableCell>
                <IngestionBadge state={documentState(document, generations, environment)} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <p className="border-t border-line px-[18px] py-2 text-sm text-ink-3">
        {t("knowledge.count", { count: documents.length })}
      </p>
    </div>
  );
}

/** A document's ingestion: its status and, once ready, its chunks and pages; a failure's code. */
function IngestionBadge({ state }: { state: ReturnType<typeof documentState> }) {
  const { t } = useTranslation();
  if (state.kind === "deleting")
    return <Badge variant="outline">{t("knowledge.status.deleting")}</Badge>;
  if (state.kind === "absent")
    return <span className="text-sm text-ink-3">{t("knowledge.status.absent")}</span>;
  const { ingestion } = state;
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <Badge variant={INGESTION_VARIANT[ingestion.status]}>
        {t(`knowledge.status.${ingestion.status}`)}
      </Badge>
      {ingestion.status === "ready" && (
        <span className="text-sm text-ink-3">
          {t("knowledge.chunks", { count: ingestion.chunks ?? 0 })},{" "}
          {t("knowledge.pages", { count: ingestion.pages ?? 0 })}
        </span>
      )}
      {ingestion.status === "failed" && ingestion.errorCode && (
        <code className="text-sm">{ingestion.errorCode}</code>
      )}
    </span>
  );
}
